import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { fetchMaterialSn } from '#/api/imsMgmt/inspectionReport/fetchMaterialSn';
import { fetchSnSalesWarranty } from '#/api/crmMgmt/snSalesWarranty';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import SnWarrantyCorrectionForm from '#/modules/crmMgmt/salesOrder/components/SnWarrantyCorrectionForm';
import {
  SN_WARRANTY_SURFACE_CLASS,
  formatSnStatus,
  formatWarrantyPolicy,
  formatWarrantySource,
  getSnSalesWarrantyErrorMessage,
  normalizeWarrantyHistory,
} from '#/modules/crmMgmt/salesOrder/helper/snSalesWarranty';
import { formatDateOrDash, formatDateTimeOrDash } from '#/utils/date';

function formatCustomer(customer) {
  return [customer?.code, customer?.name].filter(Boolean).join(' - ') || '—';
}

function formatChange(previous, next, format = formatDateOrDash) {
  if (previous === next || previous == null) {
    return format(next);
  }
  return `${format(previous)} → ${format(next)}`;
}

const SummaryRow = ({ label, children }) => (
  <div className="flex gap-2 py-1">
    <dt className="w-20 shrink-0 text-gray-500">{label}</dt>
    <dd className="min-w-0 text-gray-900">{children}</dd>
  </div>
);

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * 以完整序號查保固：先用序號登錄取得 mtr_sn_id，再讀銷售／保固與更正歷史。
 * 查詢中的序號由頁面持有（切 tab、直接售出成功後帶入都不會遺失）。
 */
const SnWarrantyLookupSection = ({ serialNumber, onSearch, canCorrect }) => {
  const [input, setInput] = useState(serialNumber);

  const registryQuery = useQuery({
    queryKey: ['erp', 'materialSn', serialNumber],
    queryFn: () => fetchMaterialSn(serialNumber),
    enabled: Boolean(serialNumber),
    retry: false,
  });
  const mtrSnId = registryQuery.data?.data?.materialSn?.id ?? '';

  const warrantyQuery = useQuery({
    queryKey: ['erp', 'snSalesWarranty', mtrSnId],
    queryFn: () => fetchSnSalesWarranty(mtrSnId),
    enabled: Boolean(mtrSnId),
    retry: false,
  });
  const warranty = warrantyQuery.data?.data;
  const history = normalizeWarrantyHistory(warranty?.history);

  let errorMessage = '';
  if (registryQuery.isError) {
    // 序號登錄查詢屬檢測報告權限（ims.inspection_report.read）
    errorMessage = registryQuery.error?.response?.status === 403
      ? '需要檢測報告讀取權限才能以序號查詢'
      : getSnSalesWarrantyErrorMessage(registryQuery.error, '查不到此序號');
  } else if (registryQuery.isSuccess && !mtrSnId) {
    errorMessage = '此序號沒有有效的登錄資料';
  } else if (warrantyQuery.isError) {
    errorMessage = warrantyQuery.error?.response?.status === 404
      ? '此序號尚未售出，沒有銷售／保固資料'
      : getSnSalesWarrantyErrorMessage(warrantyQuery.error, '讀取保固失敗');
  }

  return (
    <section className={SN_WARRANTY_SURFACE_CLASS}>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearch(input);
        }}
      >
        <FloatingInput
          id="snWarrantyLookupSerial"
          type="text"
          label="完整序號"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={() => {}}
          autoComplete="off"
          containerClassName="min-w-0 flex-1"
          suppressInlineError
        />
        <Button type="submit" disabled={!input || registryQuery.isFetching || warrantyQuery.isFetching}>
        查詢
        </Button>
      </form>

      {errorMessage ? (
        <p className="mt-3 text-sm text-destructive">{errorMessage}</p>
      ) : null}

      {warranty ? (
        <div className="mt-4 text-sm">
          <dl className="divide-y divide-gray-100">
            <SummaryRow label="序號">{warranty.serialNumber}</SummaryRow>
            <SummaryRow label="狀態">{formatSnStatus(warranty.status)}</SummaryRow>
            <SummaryRow label="客戶">{formatCustomer(warranty.customer)}</SummaryRow>
            <SummaryRow label="來源">{formatWarrantySource(warranty.source)}</SummaryRow>
            <SummaryRow label="銷售日">{formatDateOrDash(warranty.salesDate)}</SummaryRow>
            <SummaryRow label="保固政策">{formatWarrantyPolicy(warranty.warrantyPolicy)}</SummaryRow>
            <SummaryRow label="保固迄日">
              {warranty.warrantyPolicy === 'NONE' ? '無保固' : formatDateOrDash(warranty.warrantyExpiryDate)}
            </SummaryRow>
          </dl>
          <p className="mt-2 text-xs text-gray-500">客戶、來源與序號登錄不能在此更正。</p>

          {canCorrect ? (
            <SnWarrantyCorrectionForm
              mtrSnId={mtrSnId}
              warranty={warranty}
              onCorrected={() => warrantyQuery.refetch()}
            />
          ) : null}

          <h3 className="mt-5 mb-2 text-sm font-medium text-gray-900">更正歷史</h3>
          {history.length === 0 ? (
            <p className="text-gray-500">尚無更正紀錄</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded border border-gray-200">
              {history.map((entry) => (
                <li key={entry.key} className="px-3 py-2">
                  <p className="text-gray-500">
                    {`#${entry.revision} · ${formatDateTimeOrDash(entry.changedAt)}`}
                    {entry.actorName ? ` · ${entry.actorName}` : ''}
                  </p>
                  <p>{`銷售日：${formatChange(entry.previousSalesDate, entry.salesDate)}`}</p>
                  <p>
                    {`保固政策：${formatChange(
                      entry.previousWarrantyPolicy,
                      entry.warrantyPolicy,
                      formatWarrantyPolicy,
                    )}`}
                  </p>
                  <p>
                    {`保固迄日：${formatChange(
                      entry.previousWarrantyExpiryDate,
                      entry.warrantyExpiryDate,
                    )}`}
                  </p>
                  <p className="text-gray-700">{`原因：${entry.reason || '—'}`}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
};

SnWarrantyLookupSection.propTypes = {
  serialNumber: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  canCorrect: PropTypes.bool,
};

export default SnWarrantyLookupSection;
